import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { usePOCreation } from '../contextProvider/POCreationContext';
import { getUserList } from '../../../Supplier/store/Supplier.action';

function CompanyAndSupplierForPO({ isPreviewMode = false }) {
  const { t } = useTranslation('purchase-order');
  const { creationState, poProductDetails, userInfo, setCreationState, isFieldsDisabledByStatus } = usePOCreation();
  const dispatch = useDispatch();

  // Get supplier login status from Redux
  const isSupplierLoggedIn = useSelector((state) => state.UserDetails?.isSupplierLoggedIn);

  // State for contact person dropdown
  const [userList, setUserList] = useState([]);
  const [selectedContactPerson, setSelectedContactPerson] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Check if multiple different locations are being used or if selected location differs from default
  // This logic should match OrderDetailsForPO.jsx shouldShowAddressSection logic
  const shouldHideDefaultShipping = useMemo(() => {
    const orderDetails = creationState?.orderDetails || [];
    const distributionCentres = poProductDetails?.companyDetails?.distributionCentres || [];

    // Get unique locations that are actually selected in order details
    const selectedLocations = new Set();
    orderDetails.forEach((order) => {
      if (order.location) {
        selectedLocations.add(order.location);
      }
    });

    // Case 1: Multiple different locations are selected
    const hasMultipleDifferentLocations = selectedLocations.size > 1;

    // Case 2: Single location selected but it's different from the default/first distribution center
    let isDifferentFromDefault = false;
    if (selectedLocations.size === 1 && distributionCentres.length > 0) {
      const selectedLocationName = Array.from(selectedLocations)[0];
      const defaultDistributionName = distributionCentres[0]?.distributionName;
      isDifferentFromDefault = selectedLocationName !== defaultDistributionName;
    }

    return hasMultipleDifferentLocations || isDifferentFromDefault;
  }, [poProductDetails?.companyDetails?.distributionCentres, creationState?.orderDetails]);

  // Fetch user list from API
  const fetchUserList = useCallback(async () => {
    try {
      setIsLoadingUsers(true);
      const uList = await dispatch(getUserList());
      if (uList?.length > 0) {
        const data = uList.map((item) => ({
          label: `${item?.firstName} ${item?.lastName}`.trim(),
          value: item?._id,
          email: item?.email,
        }));
        setUserList(data);
      }
    } catch (error) {
      // Silently handle error - could add toast notification if needed
    } finally {
      setIsLoadingUsers(false);
    }
  }, [dispatch]);

  // Initialize user list on component mount
  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  // Handle contact person selection
  const handleContactPersonChange = useCallback(
    (selectedOption) => {
      setSelectedContactPerson(selectedOption);

      // Update the creation state with contact person ID, name, and email
      // Use null instead of empty string for ObjectId fields to prevent Mongoose casting errors
      setCreationState((prev) => ({
        ...prev,
        companyDetails: {
          ...prev.companyDetails,
          contactPersonId: selectedOption?.value || null, // Fix: Use null instead of empty string for ObjectId
          contactPerson: selectedOption?.label || '',
          contactPersonEmail: selectedOption?.email || '',
        },
      }));
    },
    [setCreationState],
  );

  // Initialize selected contact person from existing data (both from product details and creation state)
  useEffect(() => {
    if (userList.length > 0) {
      // First try to find from creation state (edit mode) - check for populated contact person object
      const contactPersonData = creationState?.companyDetails?.contactPerson;
      const contactPersonId = creationState?.companyDetails?.contactPersonId;
      let contactPersonName = typeof contactPersonData === 'object' ? contactPersonData?.fullName : contactPersonData;
      let contactPersonEmail = typeof contactPersonData === 'object' ? contactPersonData?.email : creationState?.companyDetails?.contactPersonEmail;

      // If not from creation state, try from product details
      if (!contactPersonName) {
        contactPersonName = poProductDetails?.companyDetails?.contactPerson;
        contactPersonEmail = poProductDetails?.companyDetails?.contactPersonEmail;
      }

      if (contactPersonName || contactPersonId) {
        let existingContact;

        // Try to find by ID first (most reliable)
        if (contactPersonId) {
          existingContact = userList.find((user) => user.value === contactPersonId);
        }

        // If not found by ID, try to find by name
        if (!existingContact && contactPersonName) {
          existingContact = userList.find((user) => user.label === contactPersonName);
        }

        // If still not found, try to find by email
        if (!existingContact && contactPersonEmail) {
          existingContact = userList.find((user) => user.email === contactPersonEmail);
        }

        if (existingContact && (!selectedContactPerson || selectedContactPerson.value !== existingContact.value)) {
          setSelectedContactPerson(existingContact);
        }
      }
    }
  }, [poProductDetails, userList, creationState?.companyDetails, selectedContactPerson]);

  const companyDetails = useMemo(() => {
    const safeCompanyDetails =
      poProductDetails && typeof poProductDetails === 'object' && poProductDetails.companyDetails ? poProductDetails.companyDetails : {};

    const {
      companyName = '',
      companyRegistration = '',
      vatRegistrationNumber = '',
      streetAddress = '',
      buildingNumber = '',
      city = '',
      zipCode = '',
      distributionCentres = [],
      contactPerson: rawContactPerson = '',
      contactPersonEmail: rawContactPersonEmail = '',
    } = safeCompanyDetails;

    const billingAddress = `${streetAddress} ${buildingNumber} ${city} ${zipCode}`.trim();
    const distributionCentresAddress =
      Array.isArray(distributionCentres) && distributionCentres.length > 0
        ? `${distributionCentres?.[0]?.distributionName} ${distributionCentres?.[0]?.buildingNumber} ${distributionCentres?.[0]?.streetAddress} ${distributionCentres?.[0]?.city} ${distributionCentres?.[0]?.postalCode}  ${distributionCentres?.[0]?.countryName}`
        : null;

    // Use selected contact person from state if available, otherwise fall back to raw data
    const contactPersonData = creationState?.companyDetails?.contactPerson;
    const contactPerson =
      typeof contactPersonData === 'object' ? contactPersonData?.fullName : contactPersonData || selectedContactPerson?.label || rawContactPerson || '';

    const contactPersonEmail =
      typeof contactPersonData === 'object'
        ? contactPersonData?.email
        : creationState?.companyDetails?.contactPersonEmail || selectedContactPerson?.email || rawContactPersonEmail || '';

    return {
      companyName,
      companyNumber: companyRegistration,
      vatRegistrationNo: vatRegistrationNumber,
      billingAddress,
      contactPerson,
      contactPersonEmail,
      distributionCentres: distributionCentresAddress,
    };
  }, [poProductDetails, creationState, selectedContactPerson]);

  // Process user details directly from raw data
  const userDetails = useMemo(() => {
    if (!userInfo) return {};
    const { firstName = '', lastName = '', email = '' } = userInfo;
    return {
      createdBy: `${firstName} ${lastName}`.trim(),
      createdByEmail: email,
    };
  }, [userInfo]);

  // Helper function to extract supplier details
  const getSupplierDetails = useCallback(() => {
    if (!creationState?.selectedSupplier) {
      return {};
    }

    // In preview mode, use supplierDetails from creationState (populated from snapshot)
    if (creationState.isPreviewMode && creationState.supplierDetails) {
      return {
        supplierName: creationState.supplierDetails.supplierName || '',
        supplierCode: creationState.supplierDetails.supplierCode || '',
        supplierContactName: creationState.supplierDetails.supplierContactName || '',
        supplierContactEmail: creationState.supplierDetails.supplierContactEmail || '',
        supplierAddress: creationState.supplierDetails.supplierAddress || '',
      };
    }

    if (!poProductDetails?.suppliers?.length) {
      return {};
    }

    const supplier = poProductDetails.suppliers.find((s) => s._id === creationState.selectedSupplier._id) || creationState.selectedSupplier;
    const company = supplier.companies?.find((c) => c.companyName) || supplier.companies?.[0];

    const contactFirstName = company?.internalPointContact?.firstName || '';
    const contactLastName = company?.internalPointContact?.lastName || '';
    const fullContactName = `${contactFirstName} ${contactLastName}`.trim();

    return {
      supplierName: company?.supplierName || `${supplier.firstName || ''} ${supplier.lastName || ''}`.trim() || '',
      supplierCode: company?.code || supplier.code || '',
      supplierContactName: fullContactName || supplier.internalPointContact || '',
      supplierContactEmail: company?.internalPointContact?.email || supplier?.email || '',
      supplierAddress: company?.streetAddress || supplier.streetAddress || supplier.address || '',
    };
  }, [creationState?.selectedSupplier, creationState?.isPreviewMode, creationState?.supplierDetails, poProductDetails?.suppliers]);

  const getFactoryDetails = useCallback(() => {
    if (!creationState?.selectedFactory) {
      return {};
    }

    if (creationState.isPreviewMode && creationState.supplierDetails) {
      return {
        paymentTerms: creationState.supplierDetails.paymentTerms || '',
        factoryName: creationState.supplierDetails.factoryName || '',
        factoryCode: creationState.supplierDetails.factoryCode || '',
        factoryContactName: creationState.supplierDetails.factoryContactName || '',
        factoryContactEmail: creationState.supplierDetails.factoryContactEmail || '',
        factoryAddress: creationState.supplierDetails.factoryAddress || '',
      };
    }

    if (!poProductDetails?.supplierFactories?.length) {
      return {};
    }

    const factory = poProductDetails.supplierFactories.find((f) => f._id === creationState.selectedFactory._id) || creationState.selectedFactory;
    const factoryFullAddress = `${factory.streetAddress || ''} ${factory?.buildingNo || ''} ${factory?.townCity || ''} ${factory?.zipCode || ''} ${
      factory?.country || ''
    }`.trim();

    const factoryContactFirstName = factory.internalPointContact?.firstName || '';
    const factoryContactLastName = factory.internalPointContact?.lastName || '';
    const fullFactoryContactName = `${factoryContactFirstName} ${factoryContactLastName}`.trim();

    return {
      paymentTerms: factory.paymentTerms || '',
      factoryName: factory.factoryName || factory.name || '',
      factoryCode: factory.factoryCode || factory.code || '',
      factoryContactName: fullFactoryContactName || '',
      factoryContactEmail: factory.internalPointContact?.email || factory.contactPersonEmail || factory.email || '',
      factoryAddress: factoryFullAddress || factory.address || '',
    };
  }, [creationState?.selectedFactory, creationState?.isPreviewMode, creationState?.supplierDetails, poProductDetails?.supplierFactories]);

  const supplierFactoryDetails = useMemo(() => {
    return { ...getSupplierDetails(), ...getFactoryDetails() };
  }, [getSupplierDetails, getFactoryDetails]);

  const isDataLoading = creationState.isLoading;
  const autoFilledText = t('CREATE_EDIT_PO.AUTO_FILLED');
  const loadingText = t('CREATE_EDIT_PO.LOADING', 'Loading...');

  const getDisplayValue = useCallback(
    (value) => {
      if (isDataLoading) return loadingText;
      return value || autoFilledText;
    },
    [isDataLoading, loadingText, autoFilledText],
  );

  const companyDetailsP1 = useMemo(
    () => ({
      [t('CREATE_EDIT_PO.COMPANY_NAME')]: getDisplayValue(companyDetails.companyName),
      [t('CREATE_EDIT_PO.COMPANY_NUMBER')]: getDisplayValue(companyDetails.companyNumber),
      [t('CREATE_EDIT_PO.VAT_REGISTRATION_NO')]: getDisplayValue(companyDetails.vatRegistrationNo),
      [t('COMMON_LABEL.BRAND')]: creationState.selectedBrand?.name ?? creationState.selectedBrand?.label ?? '',
      [t('CREATE_EDIT_PO.PAYMENT_TERMS')]: `${getDisplayValue(supplierFactoryDetails.paymentTerms) || 0} days`,
      [t('CREATE_EDIT_PO.BILLING_ADDRESS')]: getDisplayValue(companyDetails.billingAddress),
    }),
    [t, getDisplayValue, companyDetails, supplierFactoryDetails.paymentTerms],
  );

  const companyDetailsP2 = useMemo(() => {
    const baseDetails = {
      [t('CREATE_EDIT_PO.CONTACT_PERSON')]: 'DROPDOWN_FIELD',
      [t('CREATE_EDIT_PO.CONTACT_PERSON_EMAIL')]: getDisplayValue(companyDetails.contactPersonEmail),
      [t('CREATE_EDIT_PO.CREATED_BY')]: getDisplayValue(userDetails.createdBy),
      [t('CREATE_EDIT_PO.CREATED_BY_EMAIL')]: getDisplayValue(userDetails.createdByEmail),
    };

    if (!shouldHideDefaultShipping) {
      baseDetails[t('CREATE_EDIT_PO.DEFAULT_SHIPPING_ADDRESS')] = getDisplayValue(companyDetails.distributionCentres);
    }

    return baseDetails;
  }, [t, getDisplayValue, companyDetails, userDetails, shouldHideDefaultShipping]);

  const supplierFactoryDetailsP1 = useMemo(
    () => ({
      [t('CREATE_EDIT_PO.SUPPLIER_NAME')]: getDisplayValue(supplierFactoryDetails.supplierName),
      [t('CREATE_EDIT_PO.SUPPLIER_CODE')]: getDisplayValue(supplierFactoryDetails.supplierCode),
      [t('CREATE_EDIT_PO.SUPPLIER_CONTACT_NAME')]: getDisplayValue(supplierFactoryDetails.supplierContactName),
      [t('CREATE_EDIT_PO.SUPPLIER_CONTACT_EMAIL')]: getDisplayValue(supplierFactoryDetails.supplierContactEmail),
      [t('CREATE_EDIT_PO.SUPPLIER_ADDRESS')]: getDisplayValue(supplierFactoryDetails.supplierAddress),
    }),
    [t, getDisplayValue, supplierFactoryDetails],
  );

  const supplierFactoryDetailsP2 = useMemo(
    () => ({
      [t('CREATE_EDIT_PO.FACTORY_NAME')]: getDisplayValue(supplierFactoryDetails.factoryName),
      [t('CREATE_EDIT_PO.FACTORY_CODE')]: getDisplayValue(supplierFactoryDetails.factoryCode),
      [t('CREATE_EDIT_PO.FACTORY_CONTACT_NAME')]: getDisplayValue(supplierFactoryDetails.factoryContactName),
      [t('CREATE_EDIT_PO.FACTORY_CONTACT_EMAIL')]: getDisplayValue(supplierFactoryDetails.factoryContactEmail),
      [t('CREATE_EDIT_PO.FACTORY_ADDRESS')]: getDisplayValue(supplierFactoryDetails.factoryAddress),
    }),
    [t, getDisplayValue, supplierFactoryDetails],
  );

  return (
    <div className="inner-container po-template-name p-10 mb-8">
      <div className="row">
        <div className="col-md-6">
          <div className="section-template-title">{t('CREATE_EDIT_PO.COMPANY_DETAILS')}</div>
          <div className="row">
            <div className="col-md-6">
              {Object.entries(companyDetailsP1).map(([key, value]) => (
                <div key={key} className="d-flex">
                  <div className="po-template-label text-nowrap text-medium">{key}:</div>
                  <div className="po-template-value">&nbsp;{value}</div>
                </div>
              ))}
            </div>
            <div className="col-md-6">
              {Object.entries(companyDetailsP2).map(([key, value]) => (
                <div key={key} className="d-flex align-items-start">
                  <div className="po-template-label text-nowrap text-medium">{key}:</div>
                  <div className="po-template-value ml-1 overflow-hidden" style={{ textOverflow: 'ellipsis' }}>
                    {key === t('CREATE_EDIT_PO.CONTACT_PERSON') ? (
                      <div className="w-100 m-w-150 common-react-select mb-0">
                        <Select
                          name="contactPerson"
                          options={userList}
                          className="common-select"
                          classNamePrefix="common-select"
                          placeholder={
                            isLoadingUsers ? t('CREATE_EDIT_PO.LOADING', 'Loading...') : t('CREATE_EDIT_PO.SELECT_CONTACT_PERSON', 'Select Contact Person')
                          }
                          value={selectedContactPerson}
                          onChange={handleContactPersonChange}
                          menuPosition="fixed"
                          maxMenuHeight={150}
                          isClearable
                          isLoading={isLoadingUsers}
                          isDisabled={isPreviewMode || isSupplierLoggedIn || isFieldsDisabledByStatus()}
                        />
                      </div>
                    ) : (
                      <span title={value}>{value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="section-template-title">{t('CREATE_EDIT_PO.SUPPLIER_FACTORY_DETAILS')}</div>
          <div className="row">
            <div className="col-md-6">
              {Object.entries(supplierFactoryDetailsP1).map(([key, value]) => (
                <div key={key} className="d-flex">
                  <div className="po-template-label text-nowrap text-medium">{key}:</div>
                  <div className="po-template-value">&nbsp;{value}</div>
                </div>
              ))}
            </div>
            {creationState?.selectedFactory && (
              <div className="col-md-6">
                {Object.entries(supplierFactoryDetailsP2).map(([key, value]) => (
                  <div key={key} className="d-flex">
                    <div className="po-template-label text-nowrap text-medium">{key}:</div>
                    <div className="po-template-value">&nbsp;{value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

CompanyAndSupplierForPO.propTypes = {
  isPreviewMode: PropTypes.bool,
};

CompanyAndSupplierForPO.defaultProps = {
  isPreviewMode: false,
};

export default CompanyAndSupplierForPO;
